import React from "react";
import { Grid, GridColumn as Column, GridFilterChangeEvent, GridSortChangeEvent, GridToolbar, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../messages/en-US';
import { CompositeFilterDescriptor, SortDescriptor, orderBy, filterBy } from '@progress/kendo-data-query';
import { CustomTextFilterCell } from '../../../Shared/Components/Table/CustomTextFilterCell';
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import useIdentityStore from "../../../../Zustand/identityStore";
import { pencilIcon, plusIcon, trashIcon, downloadIcon } from "@progress/kendo-svg-icons";
import AssetDialog from "../AssetDialog";
import { Dialog } from "@progress/kendo-react-dialogs";
import { getTemplateTypeDisplay } from "../../../Templates/Components/Dialogs/assetTemplateDialogUtils";
import EntityNotesButton from "../../../Dashboards/Assets/Components/Notes/EntityNotesButton";
import assetsApiService from "../../Utilities/AssetsApiService";


interface Props {
  assets: any;
  createAsset?: (values: any) => Promise<boolean>;
  updateAsset?: (asset: any) => void;
  deleteAsset?: (assetId: string) => void;
  height: number;
}

const initialFilter: CompositeFilterDescriptor = {
  logic: "and",
  filters: [],
};

function AssetsTable(props: Props) {
  const { deleteAsset } = props;
  const [selectedAsset, setSelectedAsset] = React.useState<any | null>(null);
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const currentUserRole = useIdentityStore((x) => x.user?.profile.role);
  const localizationService = useLocalization();

  const initialSort: Array<SortDescriptor> = [
  ];
  const [filter, setFilter] = React.useState(initialFilter);
  const [sort, setSort] = React.useState(initialSort);

  const _export = React.useRef<ExcelExport | null>(null);
  // Notes are managed outside this table's query, so latestNote in the cache can be stale.
  // On export, fetch fresh assets directly from the API (bypassing react-query so the page's
  // isFetching loader doesn't unmount this table), patch the fresh latestNote onto the
  // already-processed rows, then export that data.
  const excelExport = async () => {
    let exportData = processedData;
    try {
      const fresh = await assetsApiService.getAllMachines();
      const latestNoteById = new Map(fresh.map((m) => [m.id, m.latestNote]));
      exportData = processedData.map((row) =>
        latestNoteById.has(row.id) ? { ...row, latestNote: latestNoteById.get(row.id) } : row
      );
    } catch (e) {
      console.log(e);
    }
    _export.current?.save(exportData);
  };

  const handleSave = (asset: any) => {
    if (asset) {
      if (selectedAsset) {
        // Edit mode
        props.updateAsset?.(asset);
      } else {
        // Add mode
        props.createAsset?.(asset);
      }
    }
  };

  const closeHandler = () => {
    setIsDialogOpen(false);
    setSelectedAsset(null);
  };

  // Add pagination state
  const [skip, setSkip] = React.useState<number>(0);
  
  const pageChange = (event: GridPageChangeEvent) => {
    setSkip(event.page.skip);
  };

  // Update processedData to be a memoized value
  const processedData = React.useMemo(() => {
    let data = props.assets;
    
    if (filter) {
      data = filterBy(data, filter);
    }
    
    if (sort && sort.length > 0) {
      data = orderBy(data, sort);
    }
    
    return data;
  }, [props.assets, filter, sort]);

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState<boolean>(false);
  const [assetToDelete, setAssetToDelete] = React.useState<any>(null);
  const [deleteConfirmationText, setDeleteConfirmationText] = React.useState<string>("");
 
  // Reset skip when filter or sort changes
  React.useEffect(() => {
    setSkip(0);
  }, [filter, sort]);

  return (
    <div>
      <ExcelExport data={processedData} ref={_export} fileName={`${localizationService.toLanguageString('custom.assets', enMessages.custom.assets)}.xlsx`}>
        <ExcelExportColumn field="name" title={localizationService.toLanguageString('custom.name', enMessages.custom.name)} />
        <ExcelExportColumn field="manufacturer" title={localizationService.toLanguageString('custom.manufacturer', enMessages.custom.manufacturer)} />
        <ExcelExportColumn field="model" title={localizationService.toLanguageString('custom.model', enMessages.custom.model)} />
        <ExcelExportColumn field="parentName" title={localizationService.toLanguageString('custom.parentAsset', enMessages.custom.parentAsset)} />
        <ExcelExportColumn field="areaName" title={localizationService.toLanguageString('custom.location', enMessages.custom.location)} />
        <ExcelExportColumn field="machineryType" title={localizationService.toLanguageString('custom.type', enMessages.custom.type)} />
        <ExcelExportColumn field="latestNote" title={localizationService.toLanguageString('custom.notes', enMessages.custom.notes)} />
      </ExcelExport>
      <Grid
          data={processedData}
          scrollable="virtual"
          style={{
            height: props.height
          }}
          filterable={true}
          filter={filter}
          onFilterChange={(e: GridFilterChangeEvent) => setFilter(e.filter)}
          sortable={true}
          sort={sort}
          onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
          rowHeight={46}
          pageSize={30}
          total={processedData.length}
          skip={skip}
          onPageChange={pageChange}
          expandField="expanded"
          className="w-full z-0"
        >
          <GridToolbar>
            <div className="flex justify-between items-center w-full">
              <div className="gb-h3 pl-1">
                {localizationService.toLanguageString('custom.assets', enMessages.custom.assets)}
              </div>
              <div className="flex gap-2">
                {currentUserRole !== "GeneralUser" && (
                  <>
                    <Button
                      themeColor="primary"
                      onClick={() => {
                        setIsDialogOpen(true);
                      }}
                      svgIcon={plusIcon}
                    >
                      {localizationService.toLanguageString('custom.addAsset', enMessages.custom.addAsset)}
                    </Button>
                    <Button
                      themeColor="base"
                      title={localizationService.toLanguageString('custom.exportToExcel', enMessages.custom.exportToExcel)}
                      onClick={() => {
                        excelExport();
                      }}
                      svgIcon={downloadIcon}
                    />
                  </>
                )}
              </div>
            </div>
          </GridToolbar>

          <Column
            title={localizationService.toLanguageString('custom.notes', enMessages.custom.notes)}
            width="65px"
            sortable={false}
            filterable={false}
            cell={(cellProps) => (
              <td className="text-center">
                <EntityNotesButton
                  entityType="Asset"
                  entityId={cellProps.dataItem.id}
                  entityName={cellProps.dataItem.name}
                  buttonProps={{ fillMode: "flat" }}
                />
              </td>
            )}
          />
          <Column
            field="name"
            title={localizationService.toLanguageString('custom.name', enMessages.custom.name)}
            filterCell={CustomTextFilterCell}
          />
          <Column 
            field="manufacturer" 
            title={localizationService.toLanguageString('custom.manufacturer', enMessages.custom.manufacturer)}
            filterCell={CustomTextFilterCell}
          />
          <Column 
            field="model" 
            title={localizationService.toLanguageString('custom.model', enMessages.custom.model)}
            filterCell={CustomTextFilterCell}
          />
          <Column 
            field="parentName" 
            title={localizationService.toLanguageString('custom.parentAsset', enMessages.custom.parentAsset)}
            filterCell={CustomTextFilterCell}
          />
          <Column 
            field="areaName" 
            title={localizationService.toLanguageString('custom.location', enMessages.custom.location)}
            filterCell={CustomTextFilterCell}
          />
          <Column 
            field="machineryType" 
            title={localizationService.toLanguageString('custom.type', enMessages.custom.type)}
            filterCell={CustomTextFilterCell}
            cell={(props) => (
              <td>{getTemplateTypeDisplay(props.dataItem?.machineryType)}</td>
            )}
          />

          {currentUserRole !== "GeneralUser" && (
            <Column
              title={localizationService.toLanguageString('custom.actions', enMessages.custom.actions)}
              width="110px"
              sortable={false}
              filterable={false}
              cell={(props) => (
                <td className="flex flex-wrap justify-around">
                  <Button
                    themeColor={"primary"}
                    svgIcon={pencilIcon}
                    title={localizationService.toLanguageString('custom.edit', enMessages.custom.edit)}
                    onClick={() => {
                      setSelectedAsset(props.dataItem);
                      setIsDialogOpen(true);
                    }}
                  />
                   <Button
                    themeColor={"primary"}
                    svgIcon={trashIcon}
                    title={localizationService.toLanguageString('custom.delete', enMessages.custom.delete)}
                    onClick={() => {
                      setAssetToDelete(props.dataItem);
                      setDeleteDialogOpen(true);
                    }}
                  />
                </td>
              )}
            />
          )}
        </Grid>

      {isDialogOpen && (
        <AssetDialog
          onClose={closeHandler}
          asset={selectedAsset}
          onSave={handleSave}
          parentAssets={props.assets}
        />
      )}
      {deleteDialogOpen && assetToDelete && (
        <Dialog
          title={localizationService.toLanguageString('custom.deleteItems', enMessages.custom.deleteItems)}
          onClose={() => {
            setDeleteDialogOpen(false);
            setAssetToDelete(null);
            setDeleteConfirmationText("");
          }}
        >
          <div style={{ marginBottom: 24 }}>
            <strong>{assetToDelete.name}</strong> {localizationService.toLanguageString('custom.deleteItemLabel', enMessages.custom.deleteItemLabel)}
            {assetToDelete.machineryType === "HME" && (
              <>
                <div style={{ marginTop: 8}}>
                  {localizationService.toLanguageString('custom.hmeDeleteConfirmation', enMessages.custom.hmeDeleteConfirmation)}
                </div>
                <div style={{ marginTop: 30 }}>
                  <p style={{ marginBottom: 8 }}>{localizationService.toLanguageString('custom.deleteConfirmationText', enMessages.custom.deleteConfirmationText)}</p>
                  <Input
                    value={deleteConfirmationText}
                    onChange={(e) => setDeleteConfirmationText(e.target.value?.toString() || '')}
                    style={{ width: '100%' }}
                  />
                </div>
              </>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              style={{ minWidth: 120 }}
              onClick={() => {
                setDeleteDialogOpen(false);
                setAssetToDelete(null);
                setDeleteConfirmationText("");
              }}
            >
              {localizationService.toLanguageString('custom.cancel', enMessages.custom.cancel)}
            </Button>
            <Button
              style={{ minWidth: 120 }}
              themeColor="primary"
              disabled={assetToDelete.machineryType === "HME" && deleteConfirmationText.toLowerCase() !== "delete"}
              onClick={() => {
                setDeleteDialogOpen(false);
                if (assetToDelete) {
                  deleteAsset?.(assetToDelete);
                }
                setAssetToDelete(null);
                setDeleteConfirmationText("");
              }}
            >
              {localizationService.toLanguageString('custom.delete', enMessages.custom.delete)}
            </Button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

export default AssetsTable;
